// landing/main.ts
//
// Landing page: hero + boomer-and-cohort step + 4-myth tiles. Three of the
// four myths navigate on tile click. Housing reveals an extra "year house
// bought" slider plus a Launch button before navigating.

import { mountCountrySwitcher } from "../country-switcher";
import { COUNTRIES, type CountryCode } from "../data";
import { buildPonziHref } from "../shared/url";
import { mountBreadcrumb } from "../shared/breadcrumb";

type Category = "pension" | "housing" | "education" | "wages";

interface CategoryDef {
  id: Category;
  title: string;
  subtitle: string;
  image: string;
  basePath: string;
  requiresPurchaseYear: boolean;
  /** Per-country pages exist - navigate to /{basePath}/{slug}. Stubs go to /{basePath}/. */
  hasCountryRouting: boolean;
}

const CATEGORY_DEFS: CategoryDef[] = [
  {
    id: "housing",
    title: "Housing",
    subtitle: 'The "18% mortgage" myth, busted with effective lifetime rates',
    image: "/panels/housing.png",
    basePath: "/housing-ponzi",
    requiresPurchaseYear: true,
    hasCountryRouting: true,
  },
  {
    id: "pension",
    title: "Pensions",
    subtitle: "What they extract in retirement vs what they paid in",
    image: "/panels/pension.png",
    basePath: "/pension-ponzi",
    requiresPurchaseYear: false,
    hasCountryRouting: true,
  },
  {
    id: "education",
    title: "Education",
    subtitle: 'The "summer job paid for college" myth, busted on tuition curves',
    image: "/panels/education.png",
    basePath: "/education-ponzi",
    requiresPurchaseYear: false,
    hasCountryRouting: true,
  },
  {
    id: "wages",
    title: "Wages",
    subtitle: "What a minimum-wage hour bought then vs now",
    image: "/panels/wages.png",
    basePath: "/wages-ponzi",
    requiresPurchaseYear: false,
    hasCountryRouting: true,
  },
];

// Inline savings-to-deposit calculator config. Per-country assumptions taken
// from the runbook (Phase 4 spec).
interface SavingsParams {
  savingsRate: number;
  depositPct: number;
  minBuyAge: number;
}

const SAVINGS_PARAMS: Record<CountryCode, SavingsParams> = {
  au: { savingsRate: 0.12, depositPct: 0.1, minBuyAge: 25 },
  us: { savingsRate: 0.1, depositPct: 0.1, minBuyAge: 25 },
  uk: { savingsRate: 0.1, depositPct: 0.1, minBuyAge: 25 },
  ca: { savingsRate: 0.12, depositPct: 0.1, minBuyAge: 25 },
};

interface WorkYearRow {
  year: number;
  age: number;
  takeHome: number;
}
interface CohortShape {
  birthYear: number;
  workYears: WorkYearRow[];
}
interface DatasetShape {
  cohorts: CohortShape[];
}
interface HousingShape {
  national?: {
    medianHousePriceByYear?: Record<string, number>;
    averageHousePriceByYear?: Record<string, number>;
  };
}

const datasetCache = new Map<CountryCode, Promise<DatasetShape>>();
const housingCache = new Map<CountryCode, Promise<HousingShape | null>>();

function loadDataset(code: CountryCode): Promise<DatasetShape> {
  if (!datasetCache.has(code)) {
    datasetCache.set(
      code,
      fetch(`/data/${code}/dataset.json`).then((r) => {
        if (!r.ok) throw new Error(`dataset ${code} failed: ${r.status}`);
        return r.json() as Promise<DatasetShape>;
      }),
    );
  }
  return datasetCache.get(code)!;
}

function loadHousing(code: CountryCode): Promise<HousingShape | null> {
  if (!housingCache.has(code)) {
    housingCache.set(
      code,
      fetch(`/data/${code}/${code}_housing.json`)
        .then((r) => (r.ok ? (r.json() as Promise<HousingShape>) : null))
        .catch(() => null),
    );
  }
  return housingCache.get(code)!;
}

function priceForYear(housing: HousingShape | null, year: number): number | null {
  if (!housing?.national) return null;
  const table = housing.national.medianHousePriceByYear ?? housing.national.averageHousePriceByYear;
  if (!table) return null;
  const v = table[String(year)];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

async function computeDefaultPurchaseYear(
  code: CountryCode,
  birthYear: number,
): Promise<number | null> {
  const params = SAVINGS_PARAMS[code];
  try {
    const [dataset, housing] = await Promise.all([loadDataset(code), loadHousing(code)]);
    const cohort = dataset.cohorts.find((c) => c.birthYear === birthYear);
    if (!cohort) return null;

    let savings = 0;
    let fallback: number | null = null;
    for (const w of cohort.workYears) {
      savings += params.savingsRate * (w.takeHome ?? 0);
      if (w.age < params.minBuyAge) continue;
      const price = priceForYear(housing, w.year);
      if (price == null) {
        if (fallback == null) fallback = w.year;
        continue;
      }
      if (savings >= price * params.depositPct) return w.year;
    }
    return fallback;
  } catch {
    return null;
  }
}

function fallbackPurchaseYear(code: CountryCode, birthYear: number): number {
  const params = SAVINGS_PARAMS[code];
  return birthYear + params.minBuyAge + 5;
}

// --- DOM helpers ---

interface SliderHandle {
  set(year: number): void;
  get(): number;
}

interface MountSliderOpts {
  label: string;
  min: number;
  max: number;
  initial: number;
  ageBasis: "today" | "birthYear";
  birthYear: () => number;
  onChange: (year: number) => void;
}

const TODAY_YEAR = new Date().getFullYear();

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function mountSlider(host: HTMLElement, opts: MountSliderOpts): SliderHandle {
  const id = `slider-${Math.random().toString(36).slice(2, 8)}`;
  const wrap = document.createElement("div");
  wrap.className = "slider-row";
  wrap.innerHTML = `
    <div class="slider-head">
      <span class="slider-label">${opts.label}</span>
      <span class="slider-value" data-role="value">${opts.initial}</span>
    </div>
    <input
      type="range"
      class="slider-track"
      min="${opts.min}"
      max="${opts.max}"
      step="1"
      value="${opts.initial}"
      id="${id}-range"
      aria-label="${opts.label}"
    />
    <div class="slider-foot">
      <span class="slider-range">${opts.min} - ${opts.max}</span>
      <span class="age-input-wrap">
        <label for="${id}-age">${opts.ageBasis === "today" ? "Age today" : "Age at purchase"}</label>
        <input type="number" id="${id}-age" min="0" max="120" step="1" />
      </span>
    </div>
  `;
  host.appendChild(wrap);

  const range = wrap.querySelector<HTMLInputElement>(`#${id}-range`)!;
  const valueEl = wrap.querySelector<HTMLSpanElement>('[data-role="value"]')!;
  const ageInput = wrap.querySelector<HTMLInputElement>(`#${id}-age`)!;

  function refAge(year: number): number {
    return opts.ageBasis === "today" ? TODAY_YEAR - year : year - opts.birthYear();
  }
  function yearFromAge(age: number): number {
    return opts.ageBasis === "today" ? TODAY_YEAR - age : opts.birthYear() + age;
  }

  function paint(year: number) {
    valueEl.textContent = String(year);
    range.value = String(year);
    if (document.activeElement !== ageInput) {
      ageInput.value = String(refAge(year));
    }
  }

  paint(opts.initial);

  range.addEventListener("input", () => {
    const y = clamp(Number(range.value), opts.min, opts.max);
    paint(y);
    opts.onChange(y);
  });

  ageInput.addEventListener("input", () => {
    const a = Number(ageInput.value);
    if (!Number.isFinite(a)) return;
    const y = clamp(yearFromAge(a), opts.min, opts.max);
    paint(y);
    opts.onChange(y);
  });

  ageInput.addEventListener("blur", () => {
    paint(Number(range.value));
  });

  return {
    set(year) {
      paint(clamp(year, opts.min, opts.max));
    },
    get() {
      return Number(range.value);
    },
  };
}

// --- Main ---

function navigateToCategory(
  category: Category,
  country: CountryCode,
  birthYear: number,
  purchaseYear: number | null,
) {
  const def = CATEGORY_DEFS.find((d) => d.id === category)!;
  // All categories now have per-country routing. The helper builds the
  // canonical /{category}-ponzi/{slug}?cohort=YYYY[&bought=YYYY] URL.
  const href = buildPonziHref(category, country, {
    cohort: birthYear,
    bought: def.requiresPurchaseYear ? purchaseYear : null,
  });
  window.location.assign(href);
}

function wireSectionArrows() {
  for (const btn of document.querySelectorAll<HTMLButtonElement>(".section-arrow")) {
    btn.addEventListener("click", () => {
      const target = btn.dataset.target;
      if (!target) return;
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
}

function main() {
  const switcherEl = document.getElementById("boomer-row") as HTMLDivElement;
  const captionEl = document.getElementById("switcher-caption") as HTMLParagraphElement;
  const cohortHost = document.getElementById("cohort-slider-host") as HTMLDivElement;
  const tilesEl = document.getElementById("category-tiles") as HTMLDivElement;
  const housingExtra = document.getElementById("housing-extra") as HTMLDivElement;
  const purchaseHost = document.getElementById("purchase-slider-host") as HTMLDivElement;
  const goButton = document.getElementById("go-button") as HTMLButtonElement;
  const goCaption = document.getElementById("go-caption") as HTMLParagraphElement;

  let country: CountryCode = "us";
  let birthYear: number = COUNTRIES.us.defaultBirthYear;
  let purchaseYear: number | null = null;
  let purchaseSlider: SliderHandle | null = null;
  let purchaseToken = 0;

  function updateCaption() {
    const cfg = COUNTRIES[country];
    captionEl.innerHTML = `Selected <span class="country-name">${cfg.name}</span> (${cfg.character}).`;
  }

  function rebuildCohortSlider() {
    const cfg = COUNTRIES[country];
    const [minY, maxY] = cfg.cohortRange;
    birthYear = clamp(birthYear || cfg.defaultBirthYear, minY, maxY);
    cohortHost.innerHTML = "";
    mountSlider(cohortHost, {
      label: "Birth year",
      min: minY,
      max: maxY,
      initial: birthYear,
      ageBasis: "today",
      birthYear: () => birthYear,
      onChange: (y) => {
        birthYear = y;
        if (purchaseSlider) void refreshPurchaseSlider();
        updateGoCaption();
      },
    });
  }

  async function refreshPurchaseSlider() {
    const token = ++purchaseToken;
    purchaseHost.innerHTML = "";
    purchaseSlider = null;

    const params = SAVINGS_PARAMS[country];
    const minPurchase = birthYear + params.minBuyAge;
    const maxPurchase = TODAY_YEAR;

    let defaultPurchase = await computeDefaultPurchaseYear(country, birthYear);
    if (token !== purchaseToken) return;
    if (defaultPurchase == null) defaultPurchase = fallbackPurchaseYear(country, birthYear);
    defaultPurchase = clamp(defaultPurchase, minPurchase, maxPurchase);
    purchaseYear = defaultPurchase;

    purchaseSlider = mountSlider(purchaseHost, {
      label: "Year house bought",
      min: minPurchase,
      max: maxPurchase,
      initial: defaultPurchase,
      ageBasis: "birthYear",
      birthYear: () => birthYear,
      onChange: (y) => {
        purchaseYear = y;
        updateGoCaption();
      },
    });
    updateGoCaption();
  }

  function updateGoCaption() {
    if (housingExtra.hidden) return;
    const cfg = COUNTRIES[country];
    const tail = purchaseYear != null
      ? ` &middot; bought <span class="country-name">${purchaseYear}</span>`
      : "";
    goCaption.innerHTML =
      `Going to <span class="country-name">Housing Ponzi</span> for <span class="country-name">${cfg.name}</span>` +
      ` &middot; cohort <span class="country-name">${birthYear}</span>${tail}`;
  }

  // Country switcher
  mountCountrySwitcher(switcherEl, country, (code) => {
    country = code;
    birthYear = COUNTRIES[code].defaultBirthYear;
    updateCaption();
    rebuildCohortSlider();
    if (!housingExtra.hidden) void refreshPurchaseSlider();
  });
  updateCaption();
  rebuildCohortSlider();

  // Category tiles
  tilesEl.innerHTML = CATEGORY_DEFS.map(
    (def) => `
      <button type="button" class="category-tile" data-cat="${def.id}" aria-pressed="false">
        <div class="cat-title">${def.title}</div>
        <div class="cat-image-wrap">
          <img class="cat-image" src="${def.image}" alt="${def.title}" loading="lazy" />
        </div>
      </button>`,
  ).join("");

  tilesEl.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>(".category-tile");
    if (!btn) return;
    const next = btn.dataset.cat as Category;
    const def = CATEGORY_DEFS.find((d) => d.id === next);
    if (!def) return;

    for (const t of tilesEl.querySelectorAll<HTMLButtonElement>(".category-tile")) {
      const active = t === btn;
      t.dataset.active = String(active);
      t.setAttribute("aria-pressed", String(active));
    }

    if (def.requiresPurchaseYear) {
      housingExtra.hidden = false;
      void refreshPurchaseSlider();
      housingExtra.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigateToCategory(next, country, birthYear, null);
    }
  });

  goButton.addEventListener("click", () => {
    navigateToCategory("housing", country, birthYear, purchaseYear);
  });

  wireSectionArrows();
  mountBreadcrumb();
}

main();
